// A tiny client-side pub/sub for "proactive tutor nudges" (Phase 2 of the tutor
// revamp). The practice flow (and, later, other surfaces) emit a nudge when
// something happens worth reacting to — a wrong answer, a hot streak — and the
// floating tutor (TutorFab) renders it as a peek so Sage speaks up at the right
// moment instead of waiting to be clicked.
//
// Deliberately dependency-free + module-scoped (one bus per browser tab);
// nudges are ephemeral, never persisted. A global cooldown keeps Sage from
// nagging — at most one nudge per window across every source.

export type TutorNudge = {
  id: string
  /** What Sage says in the peek bubble. */
  message: string
  /** Optional prompt pre-filled into the chat input when the student opens it. */
  prefill?: string
  /** Optional question the nudge is about, so the tutor opens with full context. */
  questionId?: string
  /**
   * Optional element id to scroll to (instead of opening the chat) when the peek
   * is tapped — e.g. the daily-challenge card. When set, prefill/questionId are
   * ignored.
   */
  scrollToId?: string
}

type Listener = (nudge: TutorNudge) => void
type ClearListener = () => void

const listeners = new Set<Listener>()
const clearListeners = new Set<ClearListener>()
let lastEmitAt = -Infinity
let counter = 0

/** At most one nudge per this window, across all sources — don't nag. */
export const TUTOR_NUDGE_COOLDOWN_MS = 45_000

export function onTutorNudge(cb: Listener): () => void {
  listeners.add(cb)
  return () => {
    listeners.delete(cb)
  }
}

/**
 * Emit a nudge unless we're still inside the cooldown window. Returns true if it
 * actually fired. `nowMs` is injectable so tests don't depend on the clock.
 */
export function emitTutorNudge(nudge: Omit<TutorNudge, 'id'>, nowMs: number = Date.now()): boolean {
  if (nowMs - lastEmitAt < TUTOR_NUDGE_COOLDOWN_MS) return false
  lastEmitAt = nowMs
  const full: TutorNudge = { id: `nudge-${++counter}`, ...nudge }
  for (const cb of listeners) cb(full)
  return true
}

/**
 * The pre-answer "stuck? want a hint?" nudge. Fired after a student sits on a
 * question a while without answering. The prefill asks Sage for a HINT — a
 * nudge toward the approach — NOT the answer, so it scaffolds instead of
 * short-circuiting the struggle. Emitting it is free (just a peek); only tapping
 * it (opening the chat) spends a metered Sage message.
 */
export function stuckHintNudge(questionId: string, stem?: string | null): Omit<TutorNudge, 'id'> {
  const quoted = stemExcerpt(stem)
  // Name the question INSIDE the user's own turn, not just in the system
  // prompt's "ON SCREEN NOW" block.
  //
  // The thread is deliberately ONE rolling conversation, so by the time a
  // student taps a hint the history may hold several other questions —
  // including ones Sage posed itself. A purely deictic ask ("the question on
  // screen") then has to win against all of that on the strength of a system
  // instruction alone, and it doesn't reliably: Sage answered about an earlier
  // grammar item instead of the one the student was looking at (2026-07-27).
  //
  // Quoting the opening of the stem makes the referent explicit in the turn the
  // model is answering, which is the strongest signal available and survives
  // any amount of history. The server-side anchor stays as the belt to this
  // braces — and still carries the choices and correct answer, which this
  // deliberately does not.
  const ask = quoted
    ? `Give me a HINT for the question I'm on right now — the one that begins "${quoted}"`
    : 'Give me a HINT for the question on screen'
  return {
    message: 'Stuck? I can give you a hint — not the answer.',
    prefill: `${ask} — point me toward the approach or the first step, but do NOT reveal the answer or which choice is correct.`,
    questionId,
  }
}

/** How much of the stem to quote — enough to identify it, short enough to read. */
const STEM_EXCERPT_MAX = 80

/**
 * A short, plain-text opening of a question stem, for quoting back at Sage.
 *
 * Stems carry markdown, LaTeX and embedded ```chart blocks; none of that helps
 * identify the question and a stray backtick or quote mid-excerpt just makes
 * the prompt look broken. So: take the first line of real prose, flatten the
 * whitespace, strip the characters that would fight the surrounding quotes, and
 * cut at a word boundary.
 *
 * Returns null when there's nothing usable, so the caller falls back to the
 * deictic phrasing rather than quoting an empty string.
 */
export function stemExcerpt(stem: string | null | undefined): string | null {
  if (typeof stem !== 'string') return null
  // Skip fenced blocks ENTIRELY — their contents (a chart's JSON spec) identify
  // nothing and read as gibberish quoted back. Skipping only the ``` delimiter
  // lines would let `{"type":"bar"}` through as the excerpt.
  let inFence = false
  let firstProseLine: string | undefined
  for (const raw of stem.split('\n')) {
    const line = raw.trim()
    if (line.startsWith('```')) {
      inFence = !inFence
      continue
    }
    if (inFence) continue
    // Table rows identify nothing either.
    if (line.length === 0 || line.startsWith('|')) continue
    firstProseLine = line
    break
  }
  if (!firstProseLine) return null

  const flat = firstProseLine
    .replace(/[`"“”]/g, '') // quotes/backticks would break out of ours
    .replace(/\s+/g, ' ')
    .trim()
  if (flat.length === 0) return null
  if (flat.length <= STEM_EXCERPT_MAX) return flat

  const cut = flat.slice(0, STEM_EXCERPT_MAX)
  const lastSpace = cut.lastIndexOf(' ')
  // Only honour the word boundary if it isn't so early it loses the sense.
  const body = lastSpace > STEM_EXCERPT_MAX * 0.6 ? cut.slice(0, lastSpace) : cut
  return `${body.trimEnd()}…`
}

/**
 * Ask any surfaced nudge peek to clear itself. Fired when the moment a nudge was
 * about has passed — e.g. the student navigated to a different practice question
 * — so a stale nudge (with a now-wrong prefill like "why was my answer wrong?")
 * can't be tapped on the wrong question. No cooldown: clearing is always safe.
 */
export function clearTutorNudge(): void {
  for (const cb of clearListeners) cb()
}

/** Subscribe to nudge-clear requests. Returns an unsubscribe fn. */
export function onTutorNudgeClear(cb: ClearListener): () => void {
  clearListeners.add(cb)
  return () => {
    clearListeners.delete(cb)
  }
}

/** Test helper — reset bus state between cases. */
export function __resetTutorNudgeBus(): void {
  listeners.clear()
  clearListeners.clear()
  lastEmitAt = -Infinity
  counter = 0
}
